// Colour scales, bar widths, and number formats for the recipe-compliance report.

type Rgb = readonly [number, number, number];

const HEATMAP_TINT_STRENGTH = 0.68;

// Bars shorter than MIN are padded to a visible sliver; longer than MAX are
// clamped so the largest deviation doesn't crowd out the numeric label.
const MIN_BAR_PCT = 5.0;
const MAX_BAR_PCT = 80.0;

// PvA is clamped green at PVA_MIN and red at PVA_MAX; compliance% at
// COMPLIANCE_MIN or below is fully red, continuous above.
const PVA_MIN = -2.5;
const PVA_MAX = 10.0;
const COMPLIANCE_MIN = 90.0;

const NULL_PVA_PLACEHOLDER = "—";

const UNKNOWN_MATERIAL_NAME = "unknown";

const clamp01 = (t: number): number => Math.max(0, Math.min(1, t));

const lerpRgb = ({ from, to, t }: { from: Rgb; to: Rgb; t: number }): Rgb => [
  Math.trunc(from[0] + (to[0] - from[0]) * t),
  Math.trunc(from[1] + (to[1] - from[1]) * t),
  Math.trunc(from[2] + (to[2] - from[2]) * t),
];

const rgbHex = ([r, g, b]: Rgb): string =>
  `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;

const RGB_FUNCTION_PATTERN = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/;

// Parses the CSS colour formats the MUI theme palette uses for the colour
// anchors: `#rrggbb`, `#rgb`, `rgb(r, g, b)`, and `rgba(r, g, b, a)`.
const hexToRgb = (color: string): Rgb => {
  const rgbMatch = RGB_FUNCTION_PATTERN.exec(color);
  if (rgbMatch) {
    const [, red = "0", green = "0", blue = "0"] = rgbMatch;
    return [parseInt(red, 10), parseInt(green, 10), parseInt(blue, 10)];
  }
  const hex = color.replace("#", "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((channel) => channel + channel)
          .join("")
      : hex;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
};

// Forces a leading sign, including "+0".
const signed = (value: number, decimals: number): string =>
  `${value < 0 ? "-" : "+"}${Math.abs(value).toFixed(decimals)}`;

export interface RecipeCompliancePalette {
  green: string;
  amber: string;
  red: string;
  surface: string;
}

export interface RecipeComplianceColors {
  garColor: (t: number) => string;
  impactColor: (args: { val: number; maxAbs: number }) => string;
  complianceDotColor: (pct: number) => string;
  pvaDotColor: (val: number) => string;
  goalBarColor: (fraction: number | null) => string | undefined;
}

export const recipeComplianceColors = (
  palette: RecipeCompliancePalette
): RecipeComplianceColors => {
  const green = hexToRgb(palette.green);
  const amber = hexToRgb(palette.amber);
  const red = hexToRgb(palette.red);
  const surface = hexToRgb(palette.surface);
  const surfaceHex = rgbHex(surface);

  const garColor = (t: number): string => {
    const clamped = clamp01(t);
    return clamped <= 0.5
      ? rgbHex(lerpRgb({ from: green, to: amber, t: clamped * 2 }))
      : rgbHex(lerpRgb({ from: amber, to: red, t: (clamped - 0.5) * 2 }));
  };

  const impactColor = ({
    val,
    maxAbs,
  }: {
    val: number;
    maxAbs: number;
  }): string => {
    if (!Number.isFinite(val) || !Number.isFinite(maxAbs)) {
      return surfaceHex;
    }
    if (val === 0 || maxAbs === 0) {
      return surfaceHex;
    }
    const t = Math.sqrt(Math.min(Math.abs(val) / maxAbs, 1));
    const target =
      val > 0
        ? lerpRgb({ from: amber, to: red, t })
        : lerpRgb({ from: amber, to: green, t });
    return rgbHex(
      lerpRgb({ from: surface, to: target, t: t * HEATMAP_TINT_STRENGTH })
    );
  };

  // `pct` is on a 0..100 scale.
  const complianceDotColor = (pct: number): string =>
    garColor(clamp01((100 - pct) / (100 - COMPLIANCE_MIN)));

  const pvaDotColor = (val: number): string =>
    rgbHex(
      lerpRgb({
        from: green,
        to: red,
        t: clamp01((val - PVA_MIN) / (PVA_MAX - PVA_MIN)),
      })
    );

  // Fully met the target → green; not at all → red. Null → no bar.
  const goalBarColor = (fraction: number | null): string | undefined =>
    fraction === null ? undefined : garColor(1 - fraction);

  return {
    garColor,
    impactColor,
    complianceDotColor,
    pvaDotColor,
    goalBarColor,
  };
};

export const barPct = ({
  val,
  maxAbs,
}: {
  val: number;
  maxAbs: number;
}): number => {
  if (val === 0 || maxAbs === 0) {
    return 0;
  }
  const raw = Math.max(MIN_BAR_PCT, (Math.abs(val) / maxAbs) * MAX_BAR_PCT);
  return Math.round(raw * 10) / 10;
};

export const formatMassTons = (val: number): string =>
  Math.abs(val) >= 1000 ? `${signed(val / 1000, 1)}K t` : `${signed(val, 0)} t`;

export const formatPva = (val: number | null): string =>
  val === null ? NULL_PVA_PLACEHOLDER : `$${signed(val, 2)}/t`;

export const formatMaterialDiff = (val: number): string => {
  if (val === 0) {
    return "0";
  }
  return Math.abs(val) >= 1000 ? `${signed(val / 1000, 1)}K` : signed(val, 0);
};

export const formatFracDiffPct = (val: number): string => {
  const pct = val * 100;
  return pct === 0 ? "0" : `${signed(pct, 1)}%`;
};

export const formatImpact = (val: number): string => signed(val, 2);

// Unsigned: a price is a level, not a diff. Null when no shift in the window
// priced the material.
export const formatSpecificPrice = (val: number | null): string =>
  val === null ? NULL_PVA_PLACEHOLDER : val.toFixed(2);

export const formatMaterialName = (name: string): string =>
  name === UNKNOWN_MATERIAL_NAME ? "Unknown" : name;

export const formatScore = (fraction: number): string =>
  `${(fraction * 100).toFixed(1)}%`;

export const formatGoalPct = (fraction: number | null): string =>
  fraction === null ? NULL_PVA_PLACEHOLDER : `${Math.round(fraction * 100)}%`;

export const goalBarPct = (fraction: number | null): number =>
  fraction === null ? 0 : Math.round(fraction * 1000) / 10;
